import { cloneDeep, remove } from 'lodash';

import { DEAL_AND_REBATE_KEYS } from '../../constants/filters';
import { PRODUCT_TYPES } from '../../constants/products';
import { SORT_OPTIONS } from '../../constants/search-types';

function isQuickFilter(categoryCode) {
  return (
    typeof categoryCode === 'string' &&
    /boolean|inventoryStatus|storeInStock/.test(categoryCode)
  );
}

function combineQuickFilters(filters) {
  const quickFilters = remove(filters, filter =>
    isQuickFilter(filter.categoryCode)
  );

  const quickFiltersCategory = {
    categoryCode: 'quickFilters',
    categoryName: 'Quick Filters',
    isOpenByDefault: true,
    showMoreByDefault: true,
    values: quickFilters.reduce((acc, quickFilter) => {
      const value = quickFilter?.values?.[0];
      if (value) {
        acc.push({
          ...value,
          categoryName: 'Quick Filters',
          valueName: quickFilter.categoryName
        });
      }

      return acc;
    }, [])
  };

  filters.unshift(quickFiltersCategory);

  return filters;
}

function isDealOrRebate(categoryCode) {
  return (
    typeof categoryCode === 'string' &&
    Object.values(DEAL_AND_REBATE_KEYS).some(code => code === categoryCode)
  );
}
function createDealsAndRebates(filters) {
  const dealsAndRebates = remove(filters, filter =>
    isDealOrRebate(filter.categoryCode)
  );
  const dealRebateFilterCategory = {
    categoryCode: 'dealAndRebateFilters',
    categoryName: 'Deals and Rebates',
    isOpenByDefault: true,
    showMoreByDefault: true,
    values: dealsAndRebates.reduce((acc, dealFilter) => {
      const value = dealFilter?.values?.[0];
      if (value) {
        acc.push({
          ...value,
          categoryName: 'Deals and Rebates',
          valueName: dealFilter.categoryName
        });
      }

      return acc;
    }, [])
  };
  if (dealsAndRebates.length >= 1) {
    filters.splice(1, 0, dealRebateFilterCategory);
  }
  return filters;
}
function facetsToFilters(facets) {
  if (!facets) {
    return [];
  }

  const sortedFacets = [...facets].sort(
    (f1, f2) => (f2.priority || 0) - (f1.priority || 0)
  );

  const filters = sortedFacets.reduce((acc, facet) => {
    if (
      !facet ||
      !facet.code ||
      facet.code.includes('tires') ||
      facet.code.includes('wheels')
    ) {
      return acc;
    }
    acc.push({
      categoryCode: facet.code,
      categoryName: facet.name,
      description: facet.description,
      isOpenByDefault: true,
      showMoreByDefault: facet.needShowMore,
      values: facet.values?.map(value => ({
        categoryCode: facet.code,
        categoryName: facet.name,
        count: value?.count,
        isSelected: value?.selected,
        plpToolTip: value?.plpToolTip,
        scaleMaxVal: value?.scaleMaxVal,
        scaleMinVal: value?.scaleMinVal,
        sliderMaxVal: value?.sliderMaxVal,
        sliderMinVal: value?.sliderMinVal,
        valueCode: value?.code,
        valueName: value?.name
      }))
    });

    return acc;
  }, []);
  const clonedFilters = cloneDeep(filters);
  const dealsAndRebateFilters = createDealsAndRebates(clonedFilters);
  return combineQuickFilters(dealsAndRebateFilters);
}

function getMatchingSelectedFilterIndex(filter, filters) {
  return (filters || []).findIndex(
    f =>
      f.categoryCode === filter.categoryCode && f.valueCode === filter.valueCode
  );
}

function sortSelectedFilters(newFilters, oldFilters) {
  return newFilters.sort((f1, f2) => {
    const i1 = getMatchingSelectedFilterIndex(f1, oldFilters);
    const i2 = getMatchingSelectedFilterIndex(f2, oldFilters);

    if (i1 === -1) {
      return 1;
    } else if (i2 === -1) {
      return -1;
    } else {
      return i1 - i2;
    }
  });
}

function facetsToSelectedFilters(facets, oldFilters, query) {
  const selectedFilters = (Array.isArray(facets) ? facets : []).reduce(
    (selectedFiltersAcc, facet) => {
      if (!facet?.code) {
        return selectedFiltersAcc;
      }

      if (isQuickFilter(facet.code) && facet.values?.[0]?.selected) {
        selectedFiltersAcc.push({
          categoryCode: facet.code,
          categoryName: 'Quick Filters',
          valueCode: facet.values?.[0]?.code,
          valueName: facet.name
        });
      } else if (Array.isArray(facet.values)) {
        const valuesArr = facet.values.reduce((valuesAcc, value) => {
          if (!value?.selected) {
            return valuesAcc;
          }

          valuesAcc.push({
            categoryCode: facet.code,
            categoryName: facet.name,
            valueCode: value.code,
            valueName: value.name
          });

          return valuesAcc;
        }, []);

        selectedFiltersAcc = selectedFiltersAcc.concat(valuesArr);
      }

      return selectedFiltersAcc;
    },
    []
  );

  const priceRangeFilter = getPriceRangeFilter(query);
  if (priceRangeFilter) {
    selectedFilters.push(priceRangeFilter);
  }

  const bundlePriceRangeFilter = getPriceRangeFilter(query, true);
  if (bundlePriceRangeFilter) {
    selectedFilters.push(bundlePriceRangeFilter);
  }

  return sortSelectedFilters(selectedFilters, oldFilters);
}

function getPriceRangeFilter(query, isBundle) {
  const priceRangeMatch = (
    isBundle
      ? /bundlePriceValue:\[(\d+) TO (\d+)\]/
      : /priceValue:\[(\d+) TO (\d+)\]/
  ).exec(query);
  if (
    Array.isArray(priceRangeMatch) &&
    priceRangeMatch[1] &&
    priceRangeMatch[2]
  ) {
    return {
      categoryCode: isBundle ? 'bundlePriceValue' : 'priceValue',
      categoryName: isBundle ? 'Package Price' : 'Price Range',
      valueCode: `[${priceRangeMatch[1]} TO ${priceRangeMatch[2]}]`,
      valueName: `$${priceRangeMatch[1]} - $${priceRangeMatch[2]}`
    };
  }
}

function createProductSearchQueryFromFilters(
  filters,
  sortCode = '',
  searchText = ''
) {
  return `${searchText}:${sortCode}${(Array.isArray(filters)
    ? filters
    : []
  ).reduce((acc, filters) => {
    acc += `:${filters.categoryCode}:${filters.valueCode}`;

    return acc;
  }, '')}`;
}

function getQueryParametersFromUrl(queryParams, baseSortValue) {
  const { pathname } = window.location;
  const defaultSort = queryParams['text']
    ? 'relevance'
    : baseSortValue || 'bestSeller-asc';

  let pageNum;

  if (!queryParams['page']) {
    pageNum = 0;
  }

  try {
    pageNum = parseInt(queryParams['page']);
    if (isNaN(pageNum) || pageNum < 0) {
      pageNum = 0;
    }
  } catch (error) {
    pageNum = 0;
  }

  return {
    boltPattern: queryParams['boltPattern'],
    brand: queryParams['brand'],
    categoryCode: pathname.split('/').pop().replace('-catalog', ''),
    diameter: queryParams['diameter'],
    isSearchAll: queryParams['isSearchAll'],
    pageNum,
    query: queryParams['q'],
    searchText: queryParams['text'],
    sort: queryParams['sort'] || defaultSort,
    width: queryParams['width']
  };
}

function getSeoName(categoryCode, productType) {
  let seoName = categoryCode
    ? `${categoryCode}-${productType.toLowerCase()}`
    : '';
  seoName = /\//.test(seoName) ? seoName.split('/')[1] : seoName;
  return seoName;
}

function getNewSelectedFilters(filterToToggle, selectedFilters) {
  if (!Array.isArray(selectedFilters)) {
    return [];
  }

  const matchingSelectedFilterIdx = selectedFilters.findIndex(
    selectedFilter =>
      selectedFilter.categoryCode === filterToToggle.categoryCode &&
      (filterToToggle.type === 'slider' ||
        selectedFilter.valueCode === filterToToggle.valueCode)
  );

  let newSelectedFilters;

  if (matchingSelectedFilterIdx === -1) {
    newSelectedFilters = [...selectedFilters, filterToToggle];
  } else {
    newSelectedFilters = [
      ...selectedFilters.slice(0, matchingSelectedFilterIdx),
      ...selectedFilters.slice(matchingSelectedFilterIdx + 1)
    ];

    if (filterToToggle.type === 'slider') {
      newSelectedFilters.push(filterToToggle);
    }
  }

  return newSelectedFilters;
}

function getNormalizedSortNames(sort) {
  if (/^best/i.test(sort)) {
    return SORT_OPTIONS.BEST_SELLER;
  } else if (/^price-asc/i.test(sort)) {
    return SORT_OPTIONS.PRICE_ASC;
  } else if (/^all/i.test(sort)) {
    return SORT_OPTIONS.HIGHEST_RATED;
  } else if (/^price-desc/i.test(sort)) {
    return SORT_OPTIONS.PRICE_DESC;
  } else if (/^rel/i.test(sort)) {
    return SORT_OPTIONS.RELEVANCE;
  } else {
    return null;
  }
}

function isChipFilter(filter) {
  return (
    filter.categoryCode !== 'priceValue' &&
    filter.categoryCode !== 'bundlePriceValue'
  );
}

function getResultsText(
  results,
  searchText,
  productType,
  isPrebuiltPackageSearch
) {
  const isPlural = Array.isArray(results) && results.length !== 1;
  if (searchText) {
    return `results for ${searchText}`;
  }
  if (isPrebuiltPackageSearch) {
    return `package result${isPlural ? 's' : ''}`;
  }
  if (productType === PRODUCT_TYPES.TIRES) {
    return `tire result${isPlural ? 's' : ''}`;
  }
}

const __testOnly__ = {
  combineQuickFilters,
  getMatchingSelectedFilterIndex,
  getPriceRangeFilter,
  isQuickFilter,
  sortSelectedFilters
};

export {
  createProductSearchQueryFromFilters,
  getNewSelectedFilters,
  facetsToFilters,
  facetsToSelectedFilters,
  getQueryParametersFromUrl,
  getSeoName,
  getNormalizedSortNames,
  isChipFilter,
  getResultsText,
  __testOnly__
};
